import { MapViewerNode, Viewer3dLoadView3dOptions } from '@3ddv/dvm-internal';
import { Viewer3dService } from '@3ddv/ngx-dvm-internal';
import { CurrencyPipe } from '@angular/common';
import { ChangeDetectorRef, Component, computed, inject, Input, OnInit, Signal } from '@angular/core';
import { DvmFormatterPipe } from '@helpers/pipes/dvm-formatter.pipe';
import { AppNodes } from '@models/enums/dvm/DvmNodes.enum';
import { UiConfiguration } from '@models/interfaces/configuration/UiConfiguration.interface';
import { CustomPopupData, CustomPopupRecord, NodeTypeKey } from '@models/types/components/CustomPopup.type';
import { Translation } from '@models/types/components/Translation.type';
import { AmenitiesService } from '@services/amenities.service';
import { DvmService } from '@services/dvm.service';
import { PopoverService } from '@services/popover.service';
import { TranslationService } from '@services/translation.service';
import { UiService } from '@services/ui.service';

@Component({
  selector: 'app-map-popup',
  standalone: true,
  providers: [
    DvmFormatterPipe,
  ],
  imports: [
    CurrencyPipe
  ],
  templateUrl: './map-popup.component.html',
  styleUrl: './map-popup.component.css'
})
export class MapPopupComponent implements OnInit {
  
  // INPUTS
  @Input({required: true})
  public node!: MapViewerNode;

  // SERVICES
  private   readonly popover:     PopoverService           = inject(PopoverService);
  private   readonly dvm:         DvmService               = inject(DvmService);
  private   readonly ams:         AmenitiesService         = inject(AmenitiesService);
  private   readonly cdr:         ChangeDetectorRef        = inject(ChangeDetectorRef);
  private   readonly format:      DvmFormatterPipe         = inject(DvmFormatterPipe);
  private   readonly uiconfig:    Signal<UiConfiguration>  = inject(UiService).uiConfig;
  public    readonly translation: TranslationService       = inject(TranslationService);
  

  //TRANSLATION CONFIG
  private readonly translationConfig:     Signal<Translation>         = computed((): Translation => this.uiconfig()['Translation']);
  public  readonly translationData:       Signal<Translation['data']> = computed((): Translation['data'] => this.translationConfig().data);
  public  readonly translationEnabled:    Signal<boolean>             = computed((): boolean  => this.translationConfig().enabled);
  public  readonly customPopupEnabled:    Signal<boolean>             = computed((): boolean  => this.uiconfig().CustomPopups.enabled);
  public  readonly customPopupData:       Signal<CustomPopupData>     = computed((): CustomPopupData  => this.uiconfig().CustomPopups.data);
  public  readonly view3dDisplayEnabled:  Signal<boolean>             = computed((): boolean => this.uiconfig().View3dDisplay.enabled);
 
  // COMPONENT STATE
  protected readonly Nodes:     typeof AppNodes   = AppNodes;
  protected thumbnail!:         string | undefined;
  protected section!:           string | undefined; 
  protected row!:               string | undefined;    
  protected seat!:              string | undefined;   
  protected prices!:            string[] | undefined;
  protected customText!:        string | undefined;   
  protected customPrice!:       string | undefined;   

  // LIFECYCLE HOOKS
  ngOnInit(): void {
    this.initComponent();
  }

  // GETTERS
  protected get price(): string | undefined {
    
    return this.prices ? // Existen precios?
           this.prices.length > 1 ? // Existen precios, pero más de uno?
           Math.min(...this.prices.map(price => parseInt(price))).toString() : this.prices[0] // Si lo hay, sacamos el minimo, si no seteamos el primero ( y unico xd )
           : undefined; // No existen precios
  }

  protected get isPremium(): boolean {
    return this.node.groups.some(group => group.includes('premium'));
  }
  
  // METHODS
  private initComponent(): void {

    if(this.customPopupEnabled()) {
      this.setCustomData();
    }

    this.setTexts();

    this.getThumbnail();

    this.getPrice();

  }

  private setCustomData(): void {
    const nodeTypeKey = (this.node.type + "s") as NodeTypeKey;
    const nodeId = this.node.id;

    let customData: CustomPopupRecord | undefined;

    if (nodeTypeKey === "seats") {
      // Seat → Row → Section hierarchy
      customData = this.customPopupData().seats[nodeId];

      if (!customData) {
        // Check if seat belongs to a row
        const idParts = nodeId.split("-");
        if (idParts.length > 2) {
          const rowId = idParts.slice(0, -1).join("-");
          customData = this.customPopupData().rows[rowId];
        }
      }

      if (!customData) {
        const sectionId = nodeId.split("-")[0];
        customData = this.customPopupData().sections[sectionId];
      }

    } else if (nodeTypeKey === "sections") {
      customData = this.customPopupData().sections[nodeId];
    }

    if (!customData) return;

    const { text, price } = customData;
    if (text) this.customText = text;
    if (price) this.customPrice = price;
  }
  

  private setTexts(): void {    
    const nodeId: string[] = this.format.transform(this.node.id).split(' ');

    const translated = this.translationData()[this.node.id.split('-')[0]];
    this.section = this.translationEnabled() && translated
      ? translated
      : nodeId[0] ?? null;      
    this.row     = nodeId[1] ?? null;
    this.seat    = nodeId[2] ?? null;
  }

  private getThumbnail(): void {

    const viewer3d:    Viewer3dService           = this.dvm.viewer3d,
          viewVersion: string                    = this.uiconfig().View3dDisplay.viewVersion,
          options:     Viewer3dLoadView3dOptions = { venue_id: this.dvm.currentVenue!, view_id: this.node.id, ...(viewVersion ? { set_id: viewVersion } : {}) };

    if(!viewer3d.isInitialized()) return;

      viewer3d.has3dView(options).subscribe((res) => {

          if(res) {

            viewer3d.getThumbnail(options, true).subscribe((thumbnailUrl: string): void => {

              this.thumbnail = thumbnailUrl;
              this.cdr.markForCheck();

            });

          }
        
      });
      
    }

  private getPrice(): void {

    const amenities = this.ams.getAmenitiesBySeat(this.node.id);
    // TODO: CUANDO LA API CAMBIE LOS "null" por null se quita esta aberracion
    this.prices = amenities.map((amenity)=> (amenity.price && amenity.price !== "null") ? amenity.price : '').filter(item => item !== '') ?? [];

  }


}
